/**
 * Frontera de error del area de contenido.
 *
 * En un renderer generico esto no es opcional. Las pantallas no las escribe
 * nadie: las arma la metadata de 95 modelos, y basta con un `FieldSpec` que
 * declare una opcion que el widget no espera para que React tire abajo el arbol
 * entero. Sin frontera, eso es la aplicacion completa en blanco —barra lateral
 * incluida— y el usuario sin forma de volver.
 *
 * Se monta ALREDEDOR de las rutas y no de toda la app: si revienta una pantalla,
 * el shell sigue en pie y se puede navegar a otra. La clave es la ruta, asi que
 * cambiar de pantalla limpia el error solo.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

import { Button, ErrorState } from '@/components/shell/primitives';

interface Props {
  /** Al cambiar, se olvida el error. Se le pasa la ruta actual. */
  resetKey: string;
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidUpdate(anterior: Props): void {
    // Navegar a otra pantalla es el intento de recuperacion mas natural: si el
    // error quedara pegado, el usuario veria la pantalla rota en TODAS las rutas.
    if (this.state.error !== null && anterior.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Sin servicio de telemetria todavia: al menos queda el componente culpable
    // en la consola, que es lo que uno necesita para reproducirlo.
    console.error('[nucleo] La pantalla se cayó al dibujarse:', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;

    return (
      <div className="space-y-4">
        <ErrorState
          title="Esta pantalla no se pudo dibujar"
          message={
            error.message === ''
              ? 'El renderer genérico falló con esta metadata.'
              : error.message
          }
        />
        <div className="flex flex-wrap gap-2">
          <Button
            variant="primary"
            onClick={() => {
              this.setState({ error: null });
            }}
          >
            Reintentar
          </Button>
          <Button
            onClick={() => {
              // La version de demostracion se abre desde `file://` y usa
              // `HashRouter`: ahi `/` es la raiz del DISCO, y este boton dejaria
              // a la persona en el listado de C:\ sin forma de volver. El
              // interruptor lo fija el build offline; en el build normal
              // `import.meta.env.VITE_NUCLEO_OFFLINE` es `undefined`, la
              // condicion queda en `false` y la rama entera desaparece del
              // bundle.
              window.location.assign(import.meta.env.VITE_NUCLEO_OFFLINE === '1' ? '#/' : '/');
            }}
          >
            Ir al inicio
          </Button>
        </div>
      </div>
    );
  }
}
